/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Accordion, Button, Card, Form, Spinner } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { skipToken } from '@reduxjs/toolkit/query'
import styled from '@emotion/styled'
import moment from 'moment'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import routes from 'components/lab/constants/routes'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  ProductivityCursor,
  ProductivityMetric,
  ProductivityMetricCount,
  useExportProductivityDetailsMutation,
  useLazyGetProductivityDetailsQuery,
  useLazyGetProductivitySummaryQuery,
} from 'store/services/managementReports'
import { useGetLabUsersQuery } from 'store/services/users'

type UserOption = { label: string; value: number }

const PAGE_SIZE_DEFAULT = 20

const StyledAccordion = styled(Accordion)`
  .accordion-item {
    border: 1px solid #e9edf1;
    border-radius: 6px !important;
    margin-bottom: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }
  .container {
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }
  .accordion-button {
    padding: 14px 18px;
    background: #fff;
    box-shadow: none;
    transition: background 0.15s ease;
  }
  .accordion-button:not(.collapsed) {
    background: #f7f9fb;
    color: inherit;
    box-shadow: inset 0 -1px 0 #e9edf1;
  }
  .accordion-button:hover {
    background: #f4f7fa;
  }
  .accordion-button:focus {
    box-shadow: none;
    border-color: transparent;
  }
  .accordion-body {
    padding: 16px 18px;
    background: #fff;
  }
`

const METRIC_META: Record<ProductivityMetric, { icon: string; color: string }> = {
  [ProductivityMetric.VERIFIED_FIELDS]: { icon: 'fa-clipboard-check', color: '#2e7d32' },
  [ProductivityMetric.NOTES_MESSAGES]: { icon: 'fa-comments', color: '#1565c0' },
  [ProductivityMetric.ORDERS_CREATED]: { icon: 'fa-folder-plus', color: '#6a1b9a' },
  [ProductivityMetric.DOCUMENTS_ATTACHED]: { icon: 'fa-paperclip', color: '#00838f' },
  [ProductivityMetric.LABELS_PRINTED]: { icon: 'fa-tags', color: '#ef6c00' },
  [ProductivityMetric.DEVICE_ASSIGNMENTS]: { icon: 'fa-microchip', color: '#4e342e' },
  [ProductivityMetric.REPORTS_UPLOADED]: { icon: 'fa-file-upload', color: '#283593' },
  [ProductivityMetric.REPORTS_APPROVED]: { icon: 'fa-check-circle', color: '#00897b' },
  [ProductivityMetric.REPORTS_MODIFIED]: { icon: 'fa-edit', color: '#ad1457' },
  [ProductivityMetric.FAX_CANCELLED_RESENT]: { icon: 'fa-fax', color: '#c62828' },
}

const dash = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '-' : String(value)

const formatTime = (value: string | null): string =>
  value ? moment(value).format('MM/DD/YYYY hh:mm:ss A') : '-'

const formatRange = (from?: string, to?: string): string => {
  const fmt = (d: string) => moment(d).format('MMM D, YYYY')
  if (from && to) return `${fmt(from)} – ${fmt(to)}`
  if (from) return `From ${fmt(from)}`
  if (to) return `Until ${fmt(to)}`
  return 'All time'
}

const OrderLink = ({ orderId }: { orderId: number | null }) =>
  orderId == null ? (
    <span className="text-muted">-</span>
  ) : (
    <Link to={`${routes.index}${routes.order.edit}${orderId}`} onClick={(e) => e.stopPropagation()}>
      #{orderId}
    </Link>
  )

const DeviceLink = ({ deviceId }: { deviceId: number | null }) =>
  deviceId == null ? (
    <span className="text-muted">-</span>
  ) : (
    <Link
      to={`${routes.index}${routes.device.root}${routes.upsert}${deviceId}`}
      onClick={(e) => e.stopPropagation()}>
      #{deviceId}
    </Link>
  )

const Pill = ({ positive, children }: { positive: boolean; children: ReactNode }) => (
  <span
    css={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      color: positive ? '#1b5e20' : '#b71c1c',
      background: positive ? 'rgba(46,125,50,0.12)' : 'rgba(198,40,40,0.12)',
    }}>
    {children}
  </span>
)

const ResultBadge = ({ value }: { value: 'GOOD' | 'FAIL' | null | undefined }) => {
  if (!value) return <span className="text-muted">-</span>
  return <Pill positive={value === 'GOOD'}>{value}</Pill>
}

const ActionBadge = ({ value }: { value: string | null | undefined }) => {
  if (!value) return <span className="text-muted">-</span>
  const label = value.charAt(0).toUpperCase() + value.slice(1)
  return <Pill positive={value === 'assigned'}>{label}</Pill>
}

const ColorBadge = ({ text, color }: { text: string; color: string }) => (
  <span
    css={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      color,
      background: `${color}1f`,
    }}>
    {text}
  </span>
)

const EntryTypeBadge = ({ value }: { value: string | null | undefined }) => {
  if (!value) return <span className="text-muted">-</span>
  return (
    <ColorBadge
      text={value.charAt(0).toUpperCase() + value.slice(1)}
      color={value === 'note' ? '#1565c0' : '#00838f'}
    />
  )
}

const NoteKindBadge = ({ value, entryType }: { value: string | null | undefined; entryType?: string | null }) => {
  // Note kind only applies to notes; messages have none. Notes default to "Internal" when unset.
  if (entryType === 'message') return <span className="text-muted">-</span>
  const kind = value || 'Internal'
  return <ColorBadge text={kind} color={kind === 'Scheduling' ? '#ef6c00' : '#5a6169'} />
}

const FaxActionBadge = ({ value }: { value: string | null | undefined }) => {
  if (!value) return <span className="text-muted">-</span>
  const resent = value === 'resent'
  return <Pill positive={resent}>{resent ? 'Re-sent' : 'Cancelled'}</Pill>
}

const ORDER_COLUMN = {
  Header: 'Order ID',
  accessor: 'orderId',
  Cell: ({ value }: { value: number | null }) => <OrderLink orderId={value} />,
}

const TIME_COLUMN = {
  Header: 'Time',
  accessor: 'timestamp',
  className: 'text-nowrap',
  Cell: ({ value }: { value: string | null }) => formatTime(value),
}

const textCell = ({ value }: { value: unknown }) => dash(value)

const getColumns = (metric: ProductivityMetric): any[] => {
  switch (metric) {
    case ProductivityMetric.VERIFIED_FIELDS:
      return [
        { Header: 'Section', accessor: 'section', Cell: textCell },
        ORDER_COLUMN,
        TIME_COLUMN,
        {
          Header: 'Result',
          accessor: 'result',
          Cell: ({ value }: { value: 'GOOD' | 'FAIL' | null }) => <ResultBadge value={value} />,
        },
        { Header: 'Reason', accessor: 'reason', Cell: textCell },
      ]
    case ProductivityMetric.NOTES_MESSAGES:
      return [
        { Header: 'Text', accessor: 'text', Cell: textCell },
        TIME_COLUMN,
        ORDER_COLUMN,
        {
          Header: 'Type',
          accessor: 'entryType',
          Cell: ({ value }: { value: string | null }) => <EntryTypeBadge value={value} />,
        },
        {
          Header: 'Note Type',
          accessor: 'noteKind',
          Cell: ({ value, row }: { value: string | null; row: { original: { entryType?: string | null } } }) => (
            <NoteKindBadge value={value} entryType={row.original.entryType} />
          ),
        },
      ]
    case ProductivityMetric.ORDERS_CREATED:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        { Header: 'Patient', accessor: 'patientName', Cell: textCell },
      ]
    case ProductivityMetric.DOCUMENTS_ATTACHED:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        { Header: 'Upload Type', accessor: 'uploadType', Cell: textCell },
      ]
    case ProductivityMetric.LABELS_PRINTED:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        { Header: 'Test Group', accessor: 'testGroup', Cell: textCell },
        { Header: 'Tracking #', accessor: 'trackingNumber', Cell: textCell },
        { Header: 'Return Tracking #', accessor: 'returnTrackingNumber', Cell: textCell },
      ]
    case ProductivityMetric.DEVICE_ASSIGNMENTS:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        {
          Header: 'Device ID',
          accessor: 'deviceId',
          Cell: ({ value }: { value: number | null }) => <DeviceLink deviceId={value} />,
        },
        {
          Header: 'Action',
          accessor: 'action',
          Cell: ({ value }: { value: string | null }) => <ActionBadge value={value} />,
        },
      ]
    case ProductivityMetric.REPORTS_UPLOADED:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        { Header: 'AHI', accessor: 'ahi', Cell: textCell },
        { Header: 'HST Report Type', accessor: 'hstReportType', Cell: textCell },
        { Header: 'Date of Service', accessor: 'dateOfService', Cell: textCell },
      ]
    case ProductivityMetric.REPORTS_APPROVED:
      return [ORDER_COLUMN, TIME_COLUMN]
    case ProductivityMetric.REPORTS_MODIFIED:
      return [ORDER_COLUMN, TIME_COLUMN, { Header: 'Nights', accessor: 'nights', Cell: textCell }]
    case ProductivityMetric.FAX_CANCELLED_RESENT:
      return [
        ORDER_COLUMN,
        TIME_COLUMN,
        { Header: 'Report IDs', accessor: 'reportIds', Cell: textCell },
        {
          Header: 'Action',
          accessor: 'action',
          Cell: ({ value }: { value: string | null }) => <FaxActionBadge value={value} />,
        },
      ]
    default:
      return [ORDER_COLUMN, TIME_COLUMN]
  }
}

type MetricDetailTableProps = {
  metric: ProductivityMetric
  userId: number
  from?: string
  to?: string
}

const MetricDetailTable = ({ metric, userId, from, to }: MetricDetailTableProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  const [cursor, setCursor] = useState<ProductivityCursor | null>(null)
  const [cursorHistory, setCursorHistory] = useState<Array<ProductivityCursor | null>>([])

  const [trigger, { data, isFetching, isError }] = useLazyGetProductivityDetailsQuery()
  const [exportDetails, { isLoading: isExporting }] = useExportProductivityDetailsMutation()

  useEffect(() => {
    trigger({
      userId,
      from,
      to,
      metric,
      size: pageSize,
      cursorTimestamp: cursor?.timestamp,
      cursorEventId: cursor?.eventId,
    })
  }, [trigger, userId, from, to, metric, pageSize, cursor])

  const tableColumns = useMemo(() => getColumns(metric), [metric])

  const tableData = useMemo(
    () => ({
      items: data?.items ?? [],
      currentPage: 0,
      totalItems: data?.items?.length ?? 0,
    }),
    [data],
  )

  const canGoBack = currentPage > 0
  const canGoForward = !!data?.hasMore && !!data?.nextCursor

  const goToPage = useCallback(
    (targetPage: number) => {
      if (targetPage === currentPage) {
        return
      }

      if (targetPage === currentPage + 1) {
        if (!data?.nextCursor) {
          return
        }
        setCursorHistory((prev) => [...prev, cursor])
        setCursor(data.nextCursor)
        setCurrentPage(targetPage)
        return
      }

      if (targetPage < 0 || targetPage > currentPage) {
        return
      }

      const nextCursor = targetPage === 0 ? null : cursorHistory[targetPage] ?? null
      setCursorHistory(cursorHistory.slice(0, targetPage))
      setCursor(nextCursor)
      setCurrentPage(targetPage)
    },
    [currentPage, cursor, cursorHistory, data?.nextCursor],
  )

  const pageItems = useMemo(() => {
    const items: Array<{ kind: 'page' | 'ellipsis'; page?: number; clickable?: boolean; active?: boolean; preview?: boolean }> = []
    const startPage = Math.max(0, currentPage - 2)

    if (startPage > 0) {
      items.push({ kind: 'page', page: 0, clickable: true })
      if (startPage > 1) {
        items.push({ kind: 'ellipsis' })
      }
    }

    for (let page = startPage; page <= currentPage; page += 1) {
      items.push({
        kind: 'page',
        page,
        clickable: page < currentPage,
        active: page === currentPage,
      })
    }

    if (canGoForward) {
      items.push({
        kind: 'page',
        page: currentPage + 1,
        clickable: true,
        preview: true,
      })
    }

    return items
  }, [canGoForward, currentPage])

  const handlePrevious = useCallback(() => {
    goToPage(currentPage - 1)
  }, [currentPage, goToPage])

  const handleNext = useCallback(() => {
    goToPage(currentPage + 1)
  }, [currentPage, goToPage])

  const handlePageSize = useCallback((nextPageSize: number) => {
    setCurrentPage(0)
    setCursor(null)
    setCursorHistory([])
    setPageSize(nextPageSize)
  }, [])

  const handleExport = useCallback(async () => {
    try {
      const blob = await exportDetails({ userId, from, to, metric }).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `productivity-${metric.toLowerCase()}-${moment().format('YYYY-MM-DD')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {}
  }, [exportDetails, userId, from, to, metric])

  if (isError) {
    return <div className="alert alert-danger mb-0">Failed to load entries. Please try again.</div>
  }

  return (
    <div css={{ minHeight: data ? undefined : 160 }}>
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-success"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || (!isFetching && !tableData.totalItems)}>
          {isExporting ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : (
            <i className="fas fa-file-csv mr-1" />
          )}
          Export CSV
        </Button>
      </div>
      <TableWrapper className="card-body row">
        <DataTable
          tableKey={`productivity_${metric}`}
          data={tableData}
          status={isFetching}
          tableColumns={tableColumns}
          pageSize={pageSize}
          setCurrentPage={() => {}}
          setPageSize={handlePageSize}
          setSortOptions={() => {}}
          searchable={false}
          entityName="entries"
          tableDataEmptyText="No entries found for this metric."
        />
      </TableWrapper>
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          padding: '0 16px 16px',
          flexWrap: 'wrap',
        }}>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#6c757d',
            fontSize: 12,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
          }}>
          <span>Page</span>
          <span
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              height: 30,
              padding: '0 10px',
              borderRadius: 4,
              border: '1px solid #d6dbe3',
              background: '#fff',
              color: '#495057',
              fontSize: 13,
            }}>
            {currentPage + 1}
          </span>
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}>
          <Button
            variant="light"
            size="sm"
            css={{
              borderRadius: 4,
              border: '1px solid #ced4da',
              padding: '6px 12px',
              color: '#495057',
              background: '#fff',
              fontWeight: 600,
            }}
            onClick={handlePrevious}
            disabled={isFetching || !canGoBack}>
            <i className="fas fa-chevron-left mr-2" />
            Previous
          </Button>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 2px',
            }}>
            {pageItems.map((item, index) => {
              if (item.kind === 'ellipsis') {
                return (
                  <span
                    key={`ellipsis_${index}`}
                    css={{
                      padding: '0 2px',
                      color: '#6c757d',
                      fontWeight: 600,
                    }}>
                    ...
                  </span>
                )
              }

              const pageNumber = Number(item.page) + 1
              return (
                <button
                  key={`page_${pageNumber}`}
                  type="button"
                  onClick={() => item.clickable && goToPage(Number(item.page))}
                  disabled={isFetching || !item.clickable}
                  css={{
                    minWidth: 34,
                    height: 34,
                    padding: '0 10px',
                    borderRadius: 4,
                    border: item.active
                      ? '1px solid #007bff'
                      : item.preview
                        ? '1px solid #ced4da'
                        : '1px solid #ced4da',
                    background: item.active
                      ? '#007bff'
                      : item.preview
                        ? '#f8f9fa'
                        : '#fff',
                    color: item.active ? '#fff' : '#495057',
                    fontWeight: item.active ? 700 : 600,
                    fontSize: 13,
                    opacity: item.clickable || item.active || item.preview ? 1 : 0.75,
                    cursor: item.clickable ? 'pointer' : item.preview ? 'pointer' : 'default',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
                  }}>
                  {pageNumber}
                </button>
              )
            })}
          </div>
          <Button
            variant="light"
            size="sm"
            css={{
              borderRadius: 4,
              border: '1px solid #ced4da',
              padding: '6px 12px',
              color: '#495057',
              background: '#fff',
              fontWeight: 600,
            }}
            onClick={handleNext}
            disabled={isFetching || !canGoForward}>
            Next
            <i className="fas fa-chevron-right ml-2" />
          </Button>
        </div>
      </div>
    </div>
  )
}

const StatBlock = ({ value, label, color }: { value: number; label: string; color: string }) => (
  <div css={{ textAlign: 'right', minWidth: 52 }}>
    <div css={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color }}>
      {value.toLocaleString()}
    </div>
    <div css={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: '#9aa1a8' }}>
      {label}
    </div>
  </div>
)

const MetricHeader = ({ metric }: { metric: ProductivityMetricCount }) => {
  const meta = METRIC_META[metric.metric]
  const hasActivity = metric.count > 0
  return (
    <div css={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <div
        css={{
          flex: '0 0 auto',
          width: 42,
          height: 42,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 14,
          color: meta.color,
          background: `${meta.color}1A`,
          fontSize: 18,
        }}>
        <i className={`fas ${meta.icon}`} />
      </div>
      <div css={{ flex: '1 1 auto', minWidth: 0 }}>
        <div css={{ fontWeight: 600, fontSize: 15, color: '#2b2f33' }}>{metric.label}</div>
      </div>
      <div
        css={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          marginLeft: 12,
          marginRight: 8,
        }}>
        {metric.breakdown?.map((b) => (
          <StatBlock
            key={b.label}
            value={b.count}
            label={b.label}
            color={b.count ? meta.color : '#aeb4ba'}
          />
        ))}
        {metric.breakdown?.length ? (
          <div css={{ alignSelf: 'stretch', width: 1, background: '#e9edf1' }} />
        ) : null}
        <StatBlock
          value={metric.count}
          label="events"
          color={hasActivity ? meta.color : '#aeb4ba'}
        />
      </div>
    </div>
  )
}

export const ProductivityReport = () => {
  const canView = useLabPermission(UserPermission.VIEW_TEAM_PRODUCTIVITY_REPORTS)

  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [userSearch, setUserSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [activeKeys, setActiveKeys] = useState<string[]>([])
  const [openedKeys, setOpenedKeys] = useState<string[]>([])
  const [searchedParams, setSearchedParams] = useState<{
    userId: number
    from?: string
    to?: string
    userName: string
  } | null>(null)

  const { data: labUsersData } = useGetLabUsersQuery(
    userSearch.length >= 2 ? { search: userSearch, page: 0, size: 50 } : skipToken,
  )

  const [triggerSummary, { data: summary, isFetching, isError }] =
    useLazyGetProductivitySummaryQuery()

  const userOptions: UserOption[] = useMemo(() => {
    if (!labUsersData) return []
    const items = Array.isArray(labUsersData) ? labUsersData : (labUsersData as any)?.items || []
    return items.map((u: any) => ({
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      value: u.id,
    }))
  }, [labUsersData])

  const isValid = !!selectedUser

  const totalEvents = useMemo(
    () => (summary?.metrics ?? []).reduce((sum, m) => sum + m.count, 0),
    [summary],
  )

  const handleSearch = useCallback(() => {
    if (!isValid || !selectedUser) return
    const params = {
      userId: selectedUser.value,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    }
    setSearchedParams({ ...params, userName: selectedUser.label })
    setActiveKeys([])
    setOpenedKeys([])
    setHasSearched(true)
    triggerSummary(params)
  }, [isValid, selectedUser, dateFrom, dateTo, triggerSummary])

  const handleSelect = useCallback((keys: string | string[] | null | undefined) => {
    const arr = Array.isArray(keys) ? keys : keys ? [keys] : []
    setActiveKeys(arr)
    setOpenedKeys((prev) => Array.from(new Set([...prev, ...arr])))
  }, [])

  const handleClear = useCallback(() => {
    setSelectedUser(null)
    setDateFrom('')
    setDateTo('')
    setActiveKeys([])
    setOpenedKeys([])
    setHasSearched(false)
    setSearchedParams(null)
  }, [])

  if (!canView) {
    return (
      <div className="container-fluid">
        <Card className="mt-3 border-0 shadow-sm">
          <Card.Body className="text-center text-muted py-5">
            <i className="fas fa-lock fa-3x mb-3" css={{ opacity: 0.3 }} />
            <p className="mb-0">You do not have access to team productivity reports.</p>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="container-fluid pb-4">
      <div className="row">
        <div className="col-12">
          <Card
            className="mt-3 shadow-sm"
            css={{
              borderRadius: 6,
              background: '#fff',
              border: '1px solid #2c5282',
              color: '#2c5282',
            }}>
            <Card.Body css={{ padding: '20px 24px' }}>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="fas fa-chart-line fa-2x mr-3" css={{ opacity: 0.85 }} />
                  <div>
                    <h4 className="mb-0" css={{ fontWeight: 700 }}>
                      Productivity Report
                    </h4>
                    <small css={{ opacity: 0.8 }}>
                      {searchedParams
                        ? `${searchedParams.userName} · ${formatRange(searchedParams.from, searchedParams.to)}`
                        : 'Per-user activity breakdown'}
                    </small>
                  </div>
                </div>
                {hasSearched && !isError && (
                  <div className="text-right">
                    <div css={{ fontSize: 30, fontWeight: 700, lineHeight: 1 }}>
                      {isFetching ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        totalEvents.toLocaleString()
                      )}
                    </div>
                    <small css={{ opacity: 0.8, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      total events
                    </small>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <Card className="mt-3 border-0 shadow-sm" css={{ borderRadius: 6 }}>
            <Card.Body>
              <div className="row align-items-end">
                <div className="col-md-4 col-sm-12 mb-2">
                  <label className="mb-1 font-weight-bold">
                    User <span className="text-danger">*</span>
                  </label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Search by name..."
                    value={selectedUser}
                    onInputChange={(val) => setUserSearch(val)}
                    onChange={(option) => setSelectedUser(option as UserOption | null)}
                    options={userOptions}
                    noOptionsMessage={() =>
                      userSearch.length < 2 ? 'Type at least 2 characters...' : 'No users found'
                    }
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1 font-weight-bold">
                    Date From <small className="text-muted font-weight-normal">(optional)</small>
                  </label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1 font-weight-bold">
                    Date To <small className="text-muted font-weight-normal">(optional)</small>
                  </label>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="col-md-2 col-sm-12 mb-2 d-flex">
                  <Button
                    variant="primary"
                    className="flex-grow-1"
                    onClick={handleSearch}
                    disabled={!isValid || isFetching}>
                    {isFetching ? (
                      <Spinner animation="border" size="sm" className="mr-1" />
                    ) : (
                      <i className="fas fa-search mr-1" />
                    )}
                    Run
                  </Button>
                  <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                    Clear
                  </Button>
                </div>
              </div>
              {!isValid && (
                <div className="text-muted mt-1">
                  <small>
                    <i className="fas fa-info-circle mr-1" />
                    Select a user to run the report. Leave the date range blank to include all time.
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {hasSearched && (
        <div className="row">
          <div className="col-12">
            {isError ? (
              <Card className="mt-3 border-0 shadow-sm">
                <Card.Body>
                  <div className="alert alert-danger mb-0">
                    Failed to load the productivity report. Please check your filters and try again.
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <StyledAccordion
                className="mt-3"
                activeKey={activeKeys}
                alwaysOpen
                onSelect={handleSelect}>
                {(summary?.metrics ?? []).map((metric) => (
                  <Accordion.Item eventKey={metric.metric} key={metric.metric}>
                    <Accordion.Header>
                      <MetricHeader metric={metric} />
                    </Accordion.Header>
                    <Accordion.Body>
                      {openedKeys.includes(metric.metric) && searchedParams ? (
                        <MetricDetailTable
                          key={`${metric.metric}-${searchedParams.userId}-${searchedParams.from || ''}-${searchedParams.to || ''}`}
                          metric={metric.metric}
                          userId={searchedParams.userId}
                          from={searchedParams.from}
                          to={searchedParams.to}
                        />
                      ) : null}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </StyledAccordion>
            )}
          </div>
        </div>
      )}

      {!hasSearched && (
        <div className="row">
          <div className="col-12">
            <Card className="mt-3 border-0 shadow-sm" css={{ borderRadius: 6 }}>
              <Card.Body className="text-center text-muted py-5">
                <i className="fas fa-chart-line fa-3x mb-3" css={{ opacity: 0.25 }} />
                <p className="mb-0">Select a user and date range to view their activity.</p>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductivityReport
